import { useEffect, useState } from 'react'

/**
 * Custom React hook that evaluates a CSS media query and returns whether it matches the current viewport.
 *
 * @param query - The CSS media query string to evaluate (e.g., '(max-width: 600px)').
 * @param clientOnly - If true, evaluates the query immediately on mount (default: false).
 *   If false, it will only evaluate the query when the component mounts and on subsequent
 *   changes. This is needed to avoid hydration issues in server-rendered components.
 * @returns A boolean indicating whether the media query currently matches.
 *
 * @example
 * const isMobile = useMediaQuery('(max-width: 600px)');
 */
const useMediaQuery = (query: string, clientOnly = false) => {
  const getMatch = () =>
    typeof window !== 'undefined' && clientOnly
      ? window.matchMedia(query).matches
      : false

  const [matches, setMatches] = useState(getMatch)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const mediaQuery = window.matchMedia(query)
    setMatches(mediaQuery.matches)

    const handler = (event: MediaQueryListEvent) => setMatches(event.matches)
    mediaQuery.addEventListener('change', handler)

    return () => mediaQuery.removeEventListener('change', handler)
  }, [query])

  return matches
}

export default useMediaQuery
